import { Injectable } from '@angular/core'
import {
  Depot,
  Producer,
  Product,
  ProductDepotMetric,
  ProductMove,
  ProductPack,
  Supplier,
  Tag,
  Vat,
} from 'depoto-core/src/entities'
import { SchemaUtil } from '../utils'
import { CoreService } from './index'
import { Stock } from '../models'
import { Currency } from 'depoto-core/src/entities/Currency'
import { ProductMdClassService } from './product-md-class.service'

@Injectable({
  providedIn: 'root',
})
export class ProductDetailService {
  public product: Product
  public productMoves: ProductMove[] = []
  public depots: Depot[] = []
  public stock: Stock[] = []
  public suppliers: Supplier[] = []
  public currencies: Currency[] = []
  public producers: Producer[] = []
  public tags: Tag[] = []
  public vats: Vat[] = []
  public productDepotMetrics: ProductDepotMetric[] = []
  public productPack: ProductPack = null
  public selectedMdClass = ''

  public isCreating: boolean
  public loading = true
  public submitted: boolean
  public isCountByPiece = true

  public piecesInPack = 1

  hasCreateRole
  hasUpdateRole
  hasDeleteRole
  hasStockOperationInRole
  hasStockOperationOutRole
  hasRoleUpdateMetrics
  hasRoleCreateMetrics

  isProductFromTemplate = false
  canViewPurchaseOrders = false

  constructor(
    public core: CoreService,
    public productMdClassService: ProductMdClassService,
  ) {
    this.canViewPurchaseOrders =
      this.core.services.user.hasRole('CLIENT_MENU_PURCHASE_ORDERS') &&
      this.core.services.user.user.company.moduleRelations?.some(
        moduleRelation => moduleRelation.module.id === 'purchase-orders',
      )
  }

  async getProduct(id: number): Promise<Product> {
    try {
      const result = await this.core.services.product.getById(id, SchemaUtil.product)
      if (result) {
        this.product = result
        this.initMdClass()
      }
    } catch (err) {
      console.log(err)
    }
    return this.product
  }

  initMdClass(): void {
    const val = this.productMdClassService.getCurrentValue(this.product)
    if (val || !this.isCreating) {
      this.selectedMdClass = val
    }
  }

  getCoreEntityLists(): void {
    this.depots = this.core.lists.depots.filter(
      depot => depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id),
    )
    this.suppliers = this.core.lists.suppliers
    this.tags = this.core.lists.tags
    this.vats = this.core.lists.vats
    this.producers = this.core.lists.producers
    this.currencies = this.core.lists.currencies
  }

  getRoles() {
    this.hasCreateRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasUpdateRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_UPDATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasDeleteRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_DELETE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasRoleUpdateMetrics =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_UPDATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasRoleCreateMetrics =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasStockOperationInRole =
      this.core.services.user.hasRole('CLIENT_MENU_IN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasStockOperationOutRole =
      this.core.services.user.hasRole('CLIENT_MENU_OUT') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
  }

  public formatStock(): void {
    this.stock = []
    this.depots.forEach(depot => {
      this.stock.push({
        depot: depot,
        items: [],
        purchaseOrderItems: [],
        sumAll: 0,
      })
    })
    this.product.productDepots.forEach(depot => {
      this.stock.forEach((stockItem, index) => {
        if (depot.depot.id === stockItem.depot.id) {
          const prod: any = {
            id: this.product.id,
            name: this.product.name,
            purchaseCurrency: this.product.purchaseCurrency,
          }
          depot.product = prod
          stockItem.items.push(depot)
          this.stock[index].sumAll += +depot.quantityStock.toFixed(2)
        }
      })
    })
    this.getLastStockItemData()
  }

  private getLastStockItemData(): void {
    this.stock.forEach(stock => {
      // @ts-ignore
      stock.items.sort((a, b) => (a.id === b.id ? 0 : a.id < b.id))
    })
    this.stock.sort(this.sortStock)
    if (this.stock[0] && this.stock[0].items[0]) {
      this.core.services.storage.set(
        this.core.services.storage.keys.views.lastStockItemSupplier,
        this.stock[0].items[0].supplier.id,
      )
      this.core.services.storage.set(
        this.core.services.storage.keys.views.lastStockItemDepot,
        this.stock[0].items[0].depot.id,
      )
      this.core.services.storage.set(
        this.core.services.storage.keys.views.lastStockItemPurchasePrice,
        this.stock[0].items[0].purchasePrice,
      )
    }
    if (this.canViewPurchaseOrders) {
      this.stock.forEach(s => {
        this.fetchItemsForStocking(s.depot.id)
      })
    }
  }

  get purchasePriceByPiece() {
    return Number((this.product.purchasePrice / this.piecesInPack).toFixed(2))
  }

  set purchasePriceByPiece(value) {
    this.product.purchasePrice = value * this.piecesInPack
  }

  private sortStock(a: Stock, b: Stock) {
    if (a.items[0] && b.items[0]) {
      if (a.items[0].id === b.items[0].id) {
        return 0
      } else {
        if (a.items[0].id < b.items[0].id) {
          return 1
        } else {
          return -1
        }
      }
    } else {
      return 0
    }
  }

  async getProductPack() {
    try {
      if (this.product?.id && this.product?.id !== 0) {
        const result = await this.core.services.productPack.getList(
          { filters: { product: this.product.id } },
          SchemaUtil.productPack,
        )
        this.productPack = result.items[0]
        this.piecesInPack = this.productPack?.quantity || 1
        this.isCountByPiece = this.productPack?.product?.tags?.length !== 0
      } else {
        this.piecesInPack = 1
        this.isCountByPiece = false
        this.productPack = null
        if (this.product) {
          this.product.tags = []
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  async fetchItemsForStocking(depotId: number) {
    const result = await this.core.services.purchase.getItemsList(
      {
        filters: {
          product: this.product.id,
          depot: depotId,
          status: 'for_stocking',
        },
      },
      SchemaUtil.purchaseOrderItems,
    )
    this.stock.forEach(s => {
      if (s.depot.id === depotId && result?.items?.length) {
        s.purchaseOrderItems = result.items
      }
    })
  }
}
